import { useCallback, useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { Search, X, FileSpreadsheet } from "lucide-react";

const SS_PREFIX = "clubops.listfilter.";
const EMPTY = { q: "", from: "", to: "", status: "__all__" };

const readState = (key) => {
  if (!key) return EMPTY;
  try {
    const raw = window.sessionStorage.getItem(SS_PREFIX + key);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch { return EMPTY; }
};

const valueAt = (row, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), row);

/** Einheitliche Filterlogik für Listenansichten: Textsuche über definierte Felder,
 *  optionaler Zeitraum über ein Datumsfeld und optionaler Statusfilter.
 *  Die Filterwerte werden je Ansicht in der Session gehalten (nicht dauerhaft). */
const csvCell = (v) => {
  if (v == null) return "";
  const s = typeof v === "number" ? String(v).replace(".", ",") : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Geldbetrag für den CSV-Export: immer zwei Nachkommastellen mit Dezimalkomma. */
export const csvMoney = (n) => Number(n || 0).toFixed(2).replace(".", ",");

/** Exportiert die aktuell gefilterte Menge als Excel-taugliche CSV (Semikolon, UTF-8 mit BOM). */
export const downloadCsv = (baseName, columns, rows) => {
  const head = columns.map((c) => csvCell(c.label)).join(";");
  const body = (rows || []).map((r) => columns.map((c) => csvCell(c.value(r))).join(";"));
  const blob = new Blob(["\uFEFF" + [head, ...body].join("\r\n") + "\r\n"], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${String(baseName).replace(/[^\w.-]+/g, "_").slice(0, 80)}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export function useListFilter(rows, { fields = [], searchOf, dateField, statusField, statusOf, storageKey } = {}) {
  const [state, setState] = useState(() => readState(storageKey));
  const fieldKey = fields.join(",");

  useEffect(() => {
    if (!storageKey) return;
    try { window.sessionStorage.setItem(SS_PREFIX + storageKey, JSON.stringify(state)); } catch { /* Session nicht verfügbar */ }
  }, [storageKey, state]);

  const patch = useCallback((p) => setState((prev) => ({ ...prev, ...p })), []);
  const getStatus = useCallback((r) => {
    if (statusOf) return statusOf(r);
    return statusField ? valueAt(r, statusField) : undefined;
  }, [statusOf, statusField]);

  const statuses = useMemo(() => {
    if (!statusField && !statusOf) return [];
    const set = new Set();
    (rows || []).forEach((r) => { const v = getStatus(r); if (v != null && v !== "") set.add(String(v)); });
    return [...set].sort((a, b) => a.localeCompare(b, "de"));
  }, [rows, statusField, statusOf, getStatus]);

  const filtered = useMemo(() => {
    const s = state.q.trim().toLowerCase();
    const list = fieldKey ? fieldKey.split(",") : [];
    return (rows || []).filter((r) => {
      if (s && !list.some((f) => {
        const v = valueAt(r, f);
        return v != null && String(v).toLowerCase().includes(s);
      }) && !(searchOf && String(searchOf(r) || "").toLowerCase().includes(s))) return false;
      if (dateField && (state.from || state.to)) {
        const d = String(valueAt(r, dateField) || "").slice(0, 10);
        if (!d) return false;
        if (state.from && d < state.from) return false;
        if (state.to && d > state.to) return false;
      }
      if (state.status !== "__all__" && String(getStatus(r) ?? "") !== state.status) return false;
      return true;
    });
  }, [rows, fieldKey, dateField, getStatus, searchOf, state]);
  return {
    q: state.q, setQ: (v) => patch({ q: v }),
    from: state.from, setFrom: (v) => patch({ from: v }),
    to: state.to, setTo: (v) => patch({ to: v }),
    status: state.status, setStatus: (v) => patch({ status: v }),
    reset: () => setState(EMPTY),
    statuses, filtered,
    active: !!(state.q.trim() || state.from || state.to || state.status !== "__all__"),
    total: (rows || []).length,
  };
}

export const ListFilter = ({ filter, placeholder, withDates, withStatus, statusLabel, testId = "list",
  exportColumns, exportName, exportRows }) => {
  const { t } = useI18n();
  const label = statusLabel || ((v) => t(`status.${v}`, v));
  const canExport = !!(exportColumns && exportColumns.length);
  const rowsForExport = exportRows || filter.filtered;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid={`${testId}-filter`}>
      <div className="relative w-full max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input value={filter.q} onChange={(e) => filter.setQ(e.target.value)}
          placeholder={placeholder || t("common.search")} data-testid={`${testId}-search`}
          className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
      </div>
      {withDates && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t("fin.from")}</span>
          <input type="date" value={filter.from} onChange={(e) => filter.setFrom(e.target.value)}
            data-testid={`${testId}-from`}
            className="h-10 rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
          <span>{t("fin.to")}</span>
          <input type="date" value={filter.to} onChange={(e) => filter.setTo(e.target.value)}
            data-testid={`${testId}-to`}
            className="h-10 rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
        </div>)}
      {withStatus && filter.statuses.length > 0 && (
        <select value={filter.status} onChange={(e) => filter.setStatus(e.target.value)}
          data-testid={`${testId}-status`} aria-label={t("common.status")}
          className="h-10 rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring">
          <option value="__all__">{`${t("common.status")}: ${t("common.all")}`}</option>
          {filter.statuses.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>)}
      {filter.active && (
        <button type="button" onClick={filter.reset} data-testid={`${testId}-reset`}
          className="flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground">
          <X className="h-3.5 w-3.5" />{t("common.reset")}
        </button>)}
      {canExport && (
        <button type="button" data-testid={`${testId}-export`}
          disabled={rowsForExport.length === 0}
          onClick={() => downloadCsv(exportName || testId, exportColumns, rowsForExport)}
          className="flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40">
          <FileSpreadsheet className="h-3.5 w-3.5" />{t("filter.exportCsv")}
        </button>)}
      <span className="text-xs text-muted-foreground" data-testid={`${testId}-count`}>
        {filter.filtered.length} / {filter.total}
      </span>
    </div>
  );
};

export default ListFilter;
